import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { ebookStore } from '../store/ebookStore'

const SectionRenderer = ({ section, chapterId, onComplete, addNote, getNote }) => {
  const [checkedItems, setCheckedItems] = useState({})
  const [calculatorName, setCalculatorName] = useState('')
  const [calculatorResult, setCalculatorResult] = useState(null)
  const [decreeIndex, setDecreeIndex] = useState(0)

  const handleCheck = (index) => {
    setCheckedItems(prev => ({ ...prev, [index]: !prev[index] }))
  }

  const calculateNumber = (name) => {
    const letterValues = {
      'A':1,'B':2,'C':3,'D':4,'E':5,'F':6,'G':7,'H':8,'I':9,
      'J':1,'K':2,'L':3,'M':4,'N':5,'O':6,'P':7,'Q':8,'R':9,
      'S':1,'T':2,'U':3,'V':4,'W':5,'X':6,'Y':7,'Z':8
    }
    
    const cleanName = name.toUpperCase().replace(/[^A-Z]/g, '')
    let total = 0
    const breakdown = []
    
    for (const letter of cleanName) {
      const value = letterValues[letter] || 0
      total += value
      breakdown.push({ letter, value })
    }
    
    let reduced = total
    while (reduced > 9 && reduced !== 11 && reduced !== 22 && reduced !== 33) {
      reduced = String(reduced).split('').reduce((a, b) => a + parseInt(b), 0)
    }
    
    return { total, reduced, breakdown }
  }

  const handleCalculate = () => {
    if (calculatorName.trim()) {
      setCalculatorResult(calculateNumber(calculatorName))
    }
  }

  switch (section.type) {
    case 'hero':
      return (
        <div className="section-hero">
          <div className="hero-glow"></div>
          <p className="hero-text">{section.content}</p>
          <button className="continue-btn" onClick={onComplete}>Continuar →</button>
        </div>
      )

    case 'text':
      return (
        <div className="section-text">
          {section.content.split('\n\n').map((p, i) => (
            <p key={i}>{p}</p>
          ))}
          <button className="continue-btn" onClick={onComplete}>Continuar →</button>
        </div>
      )

    case 'quote':
      return (
        <div className="section-quote">
          <blockquote>
            <p>{section.content}</p>
            <cite>— {section.author}</cite>
          </blockquote>
          <button className="continue-btn" onClick={onComplete}>Continuar →</button>
        </div>
      )

    case 'checklist':
      return (
        <div className="section-checklist">
          {section.title && <h3>{section.title}</h3>}
          <ul>
            {section.items.map((item, i) => (
              <li key={i}>
                <span className="check-icon">✓</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <button className="continue-btn" onClick={onComplete}>Continuar →</button>
        </div>
      )

    case 'numberTable':
      return (
        <div className="section-number-table">
          <div className="number-grid">
            {Object.entries(section.data).map(([letter, number]) => (
              <div key={letter} className="number-cell">
                <span className="letter">{letter}</span>
                <span className="number">{number}</span>
              </div>
            ))}
          </div>
          <button className="continue-btn" onClick={onComplete}>Continuar →</button>
        </div>
      )

    case 'calculator':
      return (
        <div className="section-calculator">
          <h3>{section.title}</h3>
          <p>{section.description}</p>
          <div className="calculator-input">
            <input
              type="text"
              value={calculatorName}
              onChange={(e) => setCalculatorName(e.target.value)}
              placeholder="Escribe tu nombre aquí..."
              className="name-input"
            />
            <button onClick={handleCalculate} className="calculate-btn">
              Calcular
            </button>
          </div>
          
          {calculatorResult && (
            <motion.div 
              className="calculator-result"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="result-breakdown">
                {calculatorResult.breakdown.map((item, i) => (
                  <span key={i} className="letter-value">
                    {item.letter}={item.value}
                    {i < calculatorResult.breakdown.length - 1 ? ' + ' : ''}
                  </span>
                ))}
              </div>
              <div className="result-total">
                Suma total: <strong>{calculatorResult.total}</strong>
              </div>
              <div className="result-reduced">
                Tu Número Frecuencial Maestro: <strong className="master-number">{calculatorResult.reduced}</strong>
              </div>
            </motion.div>
          )}
          <button className="continue-btn" onClick={onComplete}>Continuar →</button>
        </div>
      )

    case 'masterNumbers':
      return (
        <div className="section-master-numbers">
          <h3>{section.title}</h3>
          <div className="numbers-list">
            {section.numbers.map((item, i) => (
              <div key={i} className="number-meaning">
                <div className="number-badge">{item.num}</div>
                <div className="number-info">
                  <h4>{item.name}</h4>
                  <p>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <button className="continue-btn" onClick={onComplete}>Continuar →</button>
        </div>
      )

    case 'blockerCard':
      return (
        <div className={`section-blocker blocker-${section.blockerNumber}`}>
          <div className="blocker-header">
            <span className="blocker-number">#{section.blockerNumber}</span>
            <h3>{section.title}</h3>
            <p className="blocker-subtitle">{section.subtitle}</p>
          </div>
          
          <div className="blocker-symptoms">
            <h4>Síntomas energéticos:</h4>
            <ul>
              {section.symptoms.map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ul>
          </div>

          <div className="blocker-checklist">
            <h4>Checklist de Diagnóstico:</h4>
            {section.checklist.map((item, i) => (
              <label key={i} className="check-item">
                <input
                  type="checkbox"
                  checked={checkedItems[i] || false}
                  onChange={() => handleCheck(i)}
                />
                <span className="checkmark"></span>
                <span className="check-text">{item}</span>
              </label>
            ))}
          </div>

          <div className="blocker-result">
            <p>
              {Object.values(checkedItems).filter(Boolean).length >= section.activationThreshold
                ? `⚠️ Tu frecuencia de ${section.title.toLowerCase()} está activa y bloqueando tu flujo.`
                : `✅ Marcaste menos de ${section.activationThreshold} casillas. Este bloqueo no está activo.`}
            </p>
          </div>

          <p className="blocker-origin">{section.origin}</p>
          <button className="continue-btn" onClick={onComplete}>Continuar →</button>
        </div>
      )

    case 'journal':
      return (
        <div className="section-journal">
          <h3>{section.title}</h3>
          {section.prompts.map((prompt, i) => (
            <div key={i} className="journal-prompt">
              <label>{prompt}</label>
              <textarea
                rows={3}
                placeholder="Escribe tu respuesta aquí..."
                defaultValue={getNote(chapterId, `journal-${i}`)}
                onBlur={(e) => addNote(chapterId, `journal-${i}`, e.target.value)}
              />
            </div>
          ))}
          <button className="continue-btn" onClick={onComplete}>Continuar →</button>
        </div>
      )

    case 'dayCard':
      return (
        <div className="section-day-card">
          <div className="day-header">
            <div className="day-badge">DÍA {section.day}</div>
            <h3>{section.title}</h3>
            <div className="frequency-badge">{section.frequency}</div>
            <span className="frequency-name">{section.frequencyName}</span>
          </div>

          <div className="day-content">
            <div className="day-section">
              <h4>📜 Decreto de Poder:</h4>
              <div className="decree-text">
                <p>"{section.decree}"</p>
              </div>
            </div>

            <div className="day-section">
              <h4>🎯 Acción del día:</h4>
              <p>{section.action}</p>
            </div>

            <div className="day-section">
              <h4>🎧 Audio recomendado:</h4>
              <p>{section.audioTip}</p>
            </div>
          </div>

          <button className="continue-btn" onClick={onComplete}>Continuar →</button>
        </div>
      )

    case 'example':
      return (
        <div className="section-example">
          <h3>{section.title}</h3>
          <div className="example-calculation">
            <div className="example-name">{section.name}</div>
            <div className="example-breakdown">
              {section.calculation.map((item, i) => (
                <span key={i}>
                  {item.letter}={item.value}
                  {i < section.calculation.length - 1 ? ' + ' : ''}
                </span>
              ))}
            </div>
            <div className="example-total">
              Total: {section.total} → {section.reduced}
            </div>
          </div>
          <button className="continue-btn" onClick={onComplete}>Continuar →</button>
        </div>
      )

    case 'steps':
      return (
        <div className="section-steps">
          <h3>{section.title}</h3>
          <div className="steps-list">
            {section.steps.map((step, i) => (
              <div key={i} className="step-item">
                <div className="step-number">{i + 1}</div>
                <div className="step-content">{step}</div>
              </div>
            ))}
          </div>
          <button className="continue-btn" onClick={onComplete}>Continuar →</button>
        </div>
      )

    case 'codeCard':
      return (
        <div className="section-code-card">
          <h3>{section.title}</h3>
          <p className="code-subtitle">{section.subtitle}</p>
          <div className="code-display">{section.code}</div>
          <div className="code-instructions">
            {section.instructions.map((inst, i) => (
              <div key={i} className="instruction-step">
                <span className="step-num">{i + 1}</span>
                <span>{inst}</span>
              </div>
            ))}
          </div>
          <div className="code-duration">Duración: {section.duration}</div>
          <button className="continue-btn" onClick={onComplete}>Continuar →</button>
        </div>
      )

    case 'decreeList':
      return (
        <div className="section-decree-list">
          <h3>{section.title}</h3>
          <div className="decrees">
            {section.decrees.map((decree, i) => (
              <div 
                key={i} 
                className={`decree-item ${i === decreeIndex ? 'active' : ''}`}
                onClick={() => setDecreeIndex(i)}
              >
                <span className="decree-number">{i + 1}</span>
                <p>"{decree}"</p>
              </div>
            ))}
          </div>
          <button className="continue-btn" onClick={onComplete}>Continuar →</button>
        </div>
      )

    case 'exercise':
      return (
        <div className="section-exercise">
          <h3>{section.title}</h3>
          <p className="exercise-description">{section.description}</p>
          {section.decree && (
            <div className="exercise-decree">
              <p>"{section.decree}"</p>
            </div>
          )}
          {section.steps && (
            <ul className="exercise-steps">
              {section.steps.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ul>
          )}
          {section.duration && (
            <div className="exercise-duration">⏱️ {section.duration}</div>
          )}
          <button className="continue-btn" onClick={onComplete}>Continuar →</button>
        </div>
      )

    case 'prayer':
      return (
        <div className="section-prayer">
          <h3>{section.title}</h3>
          <div className="prayer-text">
            <p>"{section.text}"</p>
          </div>
          <button className="continue-btn" onClick={onComplete}>Continuar →</button>
        </div>
      )

    case 'compatibilityTable':
      return (
        <div className="section-compatibility">
          <h3>{section.title}</h3>
          <div className="compatibility-grid">
            {section.data.map((item, i) => (
              <div key={i} className="compatibility-row">
                <span className="compat-type">{item.type}</span>
                <span className="compat-arrow">→</span>
                <span className="compat-result">{item.result}</span>
              </div>
            ))}
          </div>
          <button className="continue-btn" onClick={onComplete}>Continuar →</button>
        </div>
      )

    case 'resourcesList':
      return (
        <div className="section-resources">
          <h3>{section.title}</h3>
          <div className="resources-grid">
            {section.resources.map((resource, i) => (
              <div key={i} className="resource-card">
                <div className="resource-icon">{resource.icon}</div>
                <div className="resource-info">
                  <h4>{resource.name}</h4>
                  <p>{resource.description}</p>
                  <span className="resource-type">Formato: {resource.type}</span>
                </div>
              </div>
            ))}
          </div>
          <button className="continue-btn" onClick={onComplete}>Continuar →</button>
        </div>
      )

    default:
      return (
        <div className="section-default">
          <p>Sección no encontrada.</p>
          <button className="continue-btn" onClick={onComplete}>Continuar →</button>
        </div>
      )
  }
}

export default SectionRenderer
